import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Account, UserRequest, Patient } from '../types';
import { EncounterVideo } from './EncounterVideo';
import { api } from '../services/api';
import { LoadingScreen } from './LoadingScreen';

interface VideoPageProps {
  account: Account;
  onNewRequest?: (request: UserRequest | null) => void;
}

export const VideoPage: React.FC<VideoPageProps> = ({ 
  account, 
  onNewRequest 
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);

  // Get URL parameters
  const roomParam = searchParams.get('room'); // If present, user is joining an existing room
  const patientId = searchParams.get('patientId') || 'default-patient';
  const noteTemplate = searchParams.get('noteTemplate') || 'PHYSICAL_SOAP';
  const taskDefinitionId = searchParams.get('taskDefinitionId') || undefined;
  
  // If joining via room link, taskDefinitionId is optional (only creator needs it)
  const isJoiningRoom = !!roomParam;

  // Fetch patients on mount
  useEffect(() => {
    const loadPatients = async () => {
      try {
        const patientsData = await api.getPatients();
        setPatients(patientsData);
      } catch (err) {
        console.error('Error fetching patients:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPatients();
  }, []);

  // Handler for when recording is completed - memoized to prevent infinite loops
  const handleRecordingComplete = useCallback((userRequestId: string) => {
    // Get patient name
    const patient = patients.find(p => p.id === patientId);
    const patientName = patient ? patient.name : 'No Patient Selected';
    
    // Create UserRequest for the animation/notification
    const newRequest: UserRequest = {
      id: userRequestId,
      userId: '',
      accountId: '',
      patientId: patientId,
      patientName: patientName,
      taskDefinitionId: taskDefinitionId || '',
      taskType: '',
      taskName: 'Video Encounter',
      status: 'Pending' as any,
      createdAt: new Date().toISOString(),
      generatedDocumentS3Keys: [],
      generatedNotes: [],
      processingContextId: '',
      logs: []
    };
    
    // Notify parent (only once)
    if (onNewRequest) {
      onNewRequest(newRequest);
    }
    
    // Navigate back to home after a short delay
    setTimeout(() => {
      navigate('/');
    }, 2000);
  }, [patients, patientId, taskDefinitionId, onNewRequest, navigate]);

  if (loading) {
    return <LoadingScreen message="Loading video page..." />;
  }

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      <div className="mb-4">
        <button 
          onClick={() => navigate('/')}
          className="btn btn-outline btn-sm"
        >
          ← Back to Home
        </button>
      </div>
      
      {isJoiningRoom && (
        <div className="alert alert-info mb-4">
          <span>🔗 Joining video call... You will be connected automatically.</span>
        </div>
      )}
      
      {!isJoiningRoom && !taskDefinitionId && (
        <div className="alert alert-warning mb-4">
          <span>⚠️ <strong>Task Definition ID is required</strong> to create a visit and generate notes. Please add <code>?taskDefinitionId=YOUR_ID</code> to the URL, or start the video call from the main uploader page.</span>
        </div>
      )}
      
      <EncounterVideo
        account={account}
        patientId={patientId}
        noteTemplate={noteTemplate}
        taskDefinitionId={taskDefinitionId}
        roomName={roomParam || undefined} // If room param exists, join that room
        onRecordingComplete={handleRecordingComplete}
        onClose={() => navigate('/')}
      />
    </div>
  );
};

